import { TMessage, loading, refresh, saveSysMessage } from "@redux/slice/api";
import store from "@redux/store";
import { logout } from "@services/auth";
import { emitAuthBlocked } from "@services/authEvents";
import { attachAuthHeaders, failAuth, refreshAuthOnce } from "@services/authInterceptor";
import { BusinessError, isSuccessResponse } from "@services/businessError";
import { AUTH_ENABLED } from "@services/env";
import { IServerResponse } from "@type/axios";
import axios, { AxiosError, CreateAxiosDefaults, InternalAxiosRequestConfig } from "axios";
import AxiosMockAdapter from "axios-mock-adapter";

// type IceCubeUserInfo = {
//   identities: {
//     userId: string;
//   }[];
// };

declare module "axios" {
  export interface AxiosRequestConfig {
    isBod?: boolean;
    useSystemLoading?: boolean;
    useSystemMessage?: boolean;
    useSysRefresh?: boolean;
  }
  export interface AxiosInstance {
    // eslint-disable-next-line no-unused-vars
    request<T = IServerResponse>(config: AxiosRequestConfig): Promise<T>;
    // eslint-disable-next-line no-unused-vars
    get<T = IServerResponse>(url: string, config?: AxiosRequestConfig): Promise<T>;
    // eslint-disable-next-line no-unused-vars
    delete<T = IServerResponse>(url: string, config?: AxiosRequestConfig): Promise<T>;
    // eslint-disable-next-line no-unused-vars
    head<T = IServerResponse>(url: string, config?: AxiosRequestConfig): Promise<T>;
    // eslint-disable-next-line no-unused-vars
    post<T = IServerResponse>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T>;
    // eslint-disable-next-line no-unused-vars
    put<T = IServerResponse>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T>;
    // eslint-disable-next-line no-unused-vars
    patch<T = IServerResponse>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T>;
  }
}

function handleResponse(msg: TMessage, config?: InternalAxiosRequestConfig, isError: Boolean = false) {
  const { dispatch } = store;
  if (config?.useSystemLoading) {
    dispatch(loading(false));
  }
  if (config?.useSystemMessage) {
    dispatch(saveSysMessage(msg));
  }
  if (config?.useSysRefresh && !isError) {
    dispatch(refresh(true));
  }
}

// Generic success messages that carry no meaning beyond "the request worked". We suppress the
// success toast for these so users aren't nagged with content-free confirmations; a meaningful
// success message (e.g. "Sign-off submitted") still surfaces.
const GENERIC_SUCCESS_MESSAGES = new Set(["success", "ok"]);

function isGenericSuccessMessage(message: unknown): boolean {
  return typeof message === "string" && GENERIC_SUCCESS_MESSAGES.has(message.trim().toLowerCase());
}

/**
 * Shared success-path handler for both the live and mock response interceptors.
 * A 2xx response can still carry a failure envelope (`isSuccess: false`); when it does we
 * surface the error toast and REJECT with a BusinessError so callers' `.then()` success
 * side-effects (redirect, close drawer, refresh) do not run on a failed request. Bodies with
 * no `isSuccess` field (blob downloads, router config) pass through unchanged.
 */
// Typed loosely (data: any / return: any) to match how the axios response interceptor is used
// across this codebase — the declaration merge at the top of this file makes callers see the
// unwrapped `response.data` payload, not an AxiosResponse.
export function onFulfilledResponse(response: { data: any; config?: InternalAxiosRequestConfig }): any {
  const success = isSuccessResponse(response.data);
  // On success, drop content-free confirmations (e.g. "Success") so GlobalMessage skips the toast.
  const content = success && isGenericSuccessMessage(response.data?.message) ? undefined : response.data?.message;
  handleResponse({ content, type: success ? "success" : "error" }, response.config, !success);
  if (!success) {
    throw new BusinessError(response.data);
  }
  return response.data;
}

// An error body is a short JSON envelope. Cap the read so an unexpectedly large payload (an HTML
// error page, a truncated CSV) is not stringified in full only to fail JSON.parse — this path is
// shared by every blob download, including the readout export.
const MAX_ERROR_BODY_BYTES = 64 * 1024;

/**
 * Read a Blob's contents as text.
 *
 * Uses FileReader rather than the newer `Blob.text()` on purpose: this app's browserslist config
 * targets Safari >= 10.1 / Chrome >= 45 / Firefox >= 45, but `Blob.text()` only exists from Safari 14 /
 * Chrome 76 / Firefox 69 and core-js does not polyfill Blob methods, so `.text()` would be
 * undefined on supported browsers. FileReader covers every target (and, unlike `Blob.text()`, is
 * implemented by jsdom, so this path is testable).
 */
function readBlobAsText(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.onerror = () => reject(reader.error);
    // Nothing calls abort() today, but the interceptor no longer settles until a reader event
    // fires, so an unhandled abort would hang the request and strand the global loading overlay.
    reader.onabort = () => resolve("");
    reader.readAsText(blob);
  });
}

/**
 * The backend contract types `message` as a string; a structured value cannot be rendered in a
 * toast, so narrow it away rather than handing back a non-string typed as one.
 */
function asMessageString(value: unknown): string | undefined {
  return typeof value === "string" ? value : undefined;
}

/**
 * Pull the backend's `message` out of an error body, whatever shape axios handed us.
 *
 * A `responseType: "blob"` request makes `error.response.data` a Blob even on failure, so the plain
 * property read that works for every JSON endpoint returns undefined and the user sees a generic
 * toast instead of the server's explanation. Every blob download in this app was affected.
 *
 * Never throws: a non-JSON body (an HTML 502 from a proxy, a truncated response) resolves to
 * undefined so the caller's own error handling stays in control.
 */
export async function extractServerMessage(data: unknown): Promise<string | undefined> {
  if (!data) {
    return undefined;
  }
  // `typeof Blob` guard: keeps this safe in any non-DOM context, where `instanceof Blob` would
  // throw a ReferenceError over the top of the error the caller is already handling.
  if (typeof Blob !== "undefined" && data instanceof Blob) {
    try {
      const parsed: unknown = JSON.parse(await readBlobAsText(data.slice(0, MAX_ERROR_BODY_BYTES)));
      return asMessageString((parsed as { message?: unknown })?.message);
    } catch {
      return undefined;
    }
  }
  return asMessageString((data as { message?: unknown })?.message);
}

const configSetting: CreateAxiosDefaults = {
  baseURL: "",
  timeout: 30000,
  useSystemLoading: true,
  useSystemMessage: true,
  useSysRefresh: false,
};

// ACTUAL REQUEST
const axiosLiveInstance = axios.create(configSetting);

axiosLiveInstance.interceptors.request.use(
  async config => {
    const { api } = store.getState();
    const { dispatch } = store;
    if (!api.loading && config.useSystemLoading) {
      dispatch(loading(true));
    }

    // Attach fresh auth token + device identity headers (shared with CopilotKit).
    const merged = await attachAuthHeaders((config.headers ?? {}) as Record<string, string>);
    Object.entries(merged).forEach(([k, v]) => {
      config.headers.set(k, v);
    });

    return config;
  },
  error => Promise.reject(error),
);

axiosLiveInstance.interceptors.response.use(onFulfilledResponse, async (error: AxiosError<IServerResponse>) => {
  const status = error.response?.status;
  const serverMessage = await extractServerMessage(error?.response?.data);
  const originalRequest = error.config;

  // Handle auth-related HTTP errors by broadcasting to the auth event bus
  switch (status) {
    case 401: {
      if (!originalRequest) {
        console.warn("Received 401 with no request config — logging out");
        failAuth("unauthorized", serverMessage);
        return Promise.reject(error);
      }

      if ((originalRequest as any)._retryAttempted) {
        console.warn("Received 401 after retry — refresh token is invalid, logging out");
        failAuth("unauthorized", serverMessage);
        return Promise.reject(error);
      }

      (originalRequest as any)._retryAttempted = true;

      // Compare against the credential that just failed, not against null. A refresh can report
      // success and hand back the same token it started with — that was the shape of the
      // hourly-logout bug, where the retry re-sent an already-rejected token and burned the one
      // retry this handler allows. Retry only when the credential actually changed.
      const previousToken = String(originalRequest.headers?.Authorization ?? "").replace(/^Bearer /, "");
      const newToken = await refreshAuthOnce();
      if (newToken && newToken !== previousToken) {
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return axiosLiveInstance(originalRequest);
      }

      console.warn("Token refresh failed — logging out");
      failAuth("unauthorized", serverMessage);
      return Promise.reject(error);
    }

    case 402:
      console.warn("Received 402 — access restricted, broadcasting auth block");
      emitAuthBlocked({ reason: "payment_required", message: serverMessage });
      return Promise.reject(error);

    case 403:
      console.warn("Received 403 — access denied, broadcasting auth block");
      emitAuthBlocked({ reason: "forbidden", message: serverMessage });
      return Promise.reject(error);
  }

  handleResponse({ content: serverMessage, type: "error" }, error?.config, true);
  return Promise.reject(error);
});

// MOCK
const axiosMockInstance = axios.create(configSetting);
axiosMockInstance.interceptors.request.use(config => {
  console.log("---------------------------------------------------");
  console.log("axios Mock request", config.url);
  if (config.useSystemLoading) {
    store.dispatch(loading(true));
  }
  if (config.data) {
    console.log("axios Mock request", config.data);
  }
  if (config.params) {
    console.log("axios Mock request", config.params);
  }
  return config;
});

axiosMockInstance.interceptors.response.use(
  response => {
    console.log("---------------------------------------------------");
    console.log("axios Mock response", response.data);
    // Mirror the live interceptor: reject on a 2xx failure envelope so dev/mock behaves like prod.
    return onFulfilledResponse(response);
  },
  async (error: AxiosError<IServerResponse>) => {
    console.log("---------------------------------------------------");
    console.log("axios Mock error", error);

    const status = error.response?.status;
    // Blob-aware, same as the live handler. The adapter is registered `onNoMatch: "passthrough"`,
    // so a developer who flips AUTH_ENABLED off for mock work still gets real backend errors here
    // and would otherwise reproduce the very bug this helper fixes.
    const serverMessage = await extractServerMessage(error?.response?.data);

    // Handle auth-related HTTP errors (same as live instance)
    switch (status) {
      case 401:
        console.warn("Mock 401 — session expired, broadcasting auth block");
        emitAuthBlocked({ reason: "unauthorized", message: serverMessage });
        logout();
        return Promise.reject(error);

      case 402:
        console.warn("Mock 402 — access restricted, broadcasting auth block");
        emitAuthBlocked({ reason: "payment_required", message: serverMessage });
        return Promise.reject(error);

      case 403:
        console.warn("Mock 403 — access denied, broadcasting auth block");
        emitAuthBlocked({ reason: "forbidden", message: serverMessage });
        return Promise.reject(error);
    }

    handleResponse({ content: serverMessage, type: "error" }, error?.config, true);
    return Promise.reject(error);
  },
);

export const axiosMockAdapterInstance = new AxiosMockAdapter(axiosMockInstance, { onNoMatch: "passthrough", delayResponse: 200 });

export default AUTH_ENABLED ? axiosLiveInstance : axiosMockInstance;
